import type { ReactNode } from 'react';
import { PaymentMethodEnum, type ExcludeOtherPaymentMethod } from '@/types/common';
import { AmazonPayForm } from '../PaymentForms/AmazonPayForm';
import { ApplePayForm } from '../PaymentForms/ApplePayForm';
import { BlikPayForm } from '../PaymentForms/BlikPayForm';
import { CreditCardForm } from '../PaymentForms/CreditCardForm';
import { GenericPayForm } from '../PaymentForms/GenericPayForm';
import { GooglePayForm } from '../PaymentForms/GooglePayForm';
import { LinkPayForm } from '../PaymentForms/LinkPayForm';
import { PaypalForm } from '../PaymentForms/Paypal';

// Forms that need a bespoke React component (provider-specific SDK widgets,
// custom UI, or framing). Anything not listed falls through to GenericPayForm,
// which dispatches to the right provider via paymentMethodsAvailable.
//
// CREDIT_CARD is `hideDisclaimer` here because this renderer is the entry
// point used inside the OTHER drawer (OtherPaymentForm renders the disclaimer
// itself). Top-level CC selection is rendered by PaymentMethodForm directly
// to keep the disclaimer visible.
//
// AMAZON_PAY routes through AmazonPayForm, which chooses between Stripe's
// Express Checkout Element (`features.preferExpressButton === true`,
// PREFERRED today) and the legacy PaymentElement (DEFAULT / SINGLE) — see
// the wrapper's docstring.
const SPECIAL_FORMS: Partial<Record<ExcludeOtherPaymentMethod, ReactNode>> = {
  [PaymentMethodEnum.CREDIT_CARD]: <CreditCardForm hideDisclaimer />,
  [PaymentMethodEnum.PAYPAL]: <PaypalForm />,
  [PaymentMethodEnum.GOOGLE_PAY]: <GooglePayForm />,
  [PaymentMethodEnum.APPLE_PAY]: <ApplePayForm />,
  [PaymentMethodEnum.AMAZON_PAY]: <AmazonPayForm />,
  [PaymentMethodEnum.LINK]: <LinkPayForm />,
  [PaymentMethodEnum.BLIK]: <BlikPayForm />,
  [PaymentMethodEnum.BLIK_CHECKOUT]: <BlikPayForm />,
};

interface PaymentMethodRendererProps {
  paymentMethod: ExcludeOtherPaymentMethod;
}

export const PaymentMethodRenderer = ({ paymentMethod }: PaymentMethodRendererProps) => {
  return SPECIAL_FORMS[paymentMethod] ?? <GenericPayForm paymentMethod={paymentMethod} />;
};
